import React from 'react';
import type { ChartType } from '@coreflow/shared';
import {
  BarChart,
  HorizontalBarChart,
  LineChart,
  PieChart,
  StackedBarChart,
  DataTable,
  BurndownChart,
  VelocityChart,
  CumulativeFlowChart,
  HistogramChart,
  WipAgingChart,
  SprintReportChart,
} from './charts';

/**
 * Props for the ChartRenderer dispatcher component.
 */
export interface ChartRendererProps {
  readonly chartType: ChartType;
  readonly data: Array<{ dimensionValue: string; measureValue: number }> | null;
  readonly dimensionLabel: string;
  readonly measureLabel: string;
  readonly hasAccess: boolean;
  readonly onSegmentClick?: (dimensionValue: string) => void;
}

/**
 * Loading skeleton displayed while data is being fetched.
 */
function LoadingState(): React.ReactElement {
  return (
    <div
      role="status"
      aria-label="Loading chart data"
      className="flex items-center justify-center h-64 w-full"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
        <span className="text-sm text-gray-500">Loading chart data…</span>
      </div>
    </div>
  );
}

/**
 * Placeholder shown when the viewer lacks access to the widget's underlying board data.
 */
function NoAccessPlaceholder(): React.ReactElement {
  return (
    <div
      role="alert"
      aria-label="No access to underlying data"
      className="flex items-center justify-center h-64 w-full rounded-lg border border-yellow-300 bg-yellow-50"
    >
      <div className="flex flex-col items-center gap-2 px-6 text-center">
        <svg
          aria-hidden="true"
          className="h-8 w-8 text-yellow-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-sm font-medium text-yellow-800">
          No access to underlying data
        </p>
        <p className="text-xs text-yellow-600">
          You do not have membership to one or more boards referenced by this widget.
        </p>
      </div>
    </div>
  );
}

/**
 * Dispatcher component that selects the appropriate chart component based on chartType.
 * Handles access control placeholders and loading states before delegating to
 * chart-type-specific components.
 *
 * Requirements: 5.3, 3.4, 6.4
 */
export function ChartRenderer({
  chartType,
  data,
  dimensionLabel,
  measureLabel,
  hasAccess,
  onSegmentClick,
}: ChartRendererProps): React.ReactElement {
  if (!hasAccess) {
    return <NoAccessPlaceholder />;
  }

  if (data === null) {
    return <LoadingState />;
  }

  const chartProps = {
    data,
    dimensionLabel,
    measureLabel,
    onSegmentClick,
  };

  switch (chartType) {
    case 'bar':
      return <BarChart {...chartProps} />;
    case 'horizontal-bar':
      return <HorizontalBarChart {...chartProps} />;
    case 'line':
      return <LineChart {...chartProps} />;
    case 'pie':
      return <PieChart {...chartProps} />;
    case 'stacked-bar':
      return <StackedBarChart {...chartProps} />;
    case 'table':
      return <DataTable {...chartProps} />;
    case 'burndown':
      return <BurndownChart {...chartProps} />;
    case 'velocity':
      return <VelocityChart {...chartProps} />;
    case 'cumulative-flow':
      return <CumulativeFlowChart {...chartProps} />;
    case 'histogram':
      return <HistogramChart {...chartProps} />;
    case 'wip-aging':
      return <WipAgingChart {...chartProps} />;
    case 'sprint-report':
      return <SprintReportChart {...chartProps} />;
    default: {
      const _exhaustive: never = chartType;
      return <div>Unsupported chart type: {_exhaustive}</div>;
    }
  }
}
